import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaPen, FaCalendarAlt, FaClock, FaExclamationTriangle, 
  FaCheck, FaSave, FaPaperPlane, FaImage, FaTimes,
  FaInfoCircle, FaLightbulb, FaLink, FaFileAlt,
  FaNewspaper, FaExternalLinkAlt, FaGlobe
} from 'react-icons/fa';
import api from '../utils/api';
import { getUser } from '../utils/auth';

// Badge clignotant pour le menu
export const PendingArticlesBadge = ({ count }) => {
  if (count === 0) return null;
  
  return (
    <motion.span
      animate={{ 
        scale: [1, 1.2, 1],
        backgroundColor: ['#ef4444', '#f97316', '#ef4444']
      }}
      transition={{ 
        repeat: Infinity, 
        duration: 1.5 
      }}
      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold"
    >
      {count}
    </motion.span>
  );
};

// Composant principal - Liste des articles à écrire
const StudentArticlesPage = () => {
  const [pendingArticles, setPendingArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journalUrl, setJournalUrl] = useState(null);
  const [publishedCount, setPublishedCount] = useState(0);

  useEffect(() => {
    loadPendingArticles();
    loadJournalInfo();
  }, []);

  const loadJournalInfo = async () => {
    try {
      const user = getUser();
      // Construire l'URL du journal de l'élève
      const schoolSlug = user?.schoolId || 'dailyglobe-school';
      const classSlug = user?.classId?.split('-').pop() || '1a';
      const currentYear = new Date().getFullYear();
      setJournalUrl(`/journal/${schoolSlug}/${classSlug}/${currentYear}`);
      
      // Charger le nombre d'articles publiés
      const response = await api.get('/articles/student/published-count');
      setPublishedCount(response.data.count || 0);
    } catch (error) {
      console.error('Error loading journal info:', error);
    }
  };

  const loadPendingArticles = async () => {
    try {
      const response = await api.get('/articles/student/pending');
      setPendingArticles(response.data.pending);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (article) => {
    if (article.isOverdue) return 'border-red-500 bg-red-50';
    if (article.isUrgent) return 'border-orange-500 bg-orange-50';
    if (article.daysRemaining <= 3) return 'border-yellow-500 bg-yellow-50';
    return 'border-blue-500 bg-blue-50';
  };

  const getStatusBadge = (article) => {
    if (article.status === 'revision_needed') {
      return <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-bold">À revoir</span>;
    }
    if (article.status === 'draft') {
      return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-bold">Brouillon</span>;
    }
    if (article.isOverdue) {
      return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-bold animate-pulse">EN RETARD</span>;
    }
    if (article.isUrgent) {
      return <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-bold">Urgent</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">À faire</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Bandeau accès journal */}
      {journalUrl && (
        <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-200 rounded-lg flex items-center justify-center">
                <FaNewspaper className="text-amber-700 text-2xl" />
              </div>
              <div>
                <h3 className="font-bold text-amber-900">Le Journal de ta Classe</h3>
                <p className="text-amber-700 text-sm">
                  {publishedCount > 0 
                    ? `${publishedCount} de tes articles publiés !` 
                    : 'Tes articles validés apparaîtront ici'}
                </p>
              </div>
            </div>
            <Link
              to={journalUrl}
              target="_blank"
              className="px-4 py-2 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 flex items-center gap-2 transition-colors shadow-md"
              data-testid="btn-view-student-journal"
            >
              <FaGlobe /> Voir le Journal
              <FaExternalLinkAlt className="text-xs" />
            </Link>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <FaPen className="text-3xl text-blue-600" />
            <PendingArticlesBadge count={pendingArticles.length} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Articles à rédiger</h1>
            <p className="text-gray-600">{pendingArticles.length} article(s) en attente</p>
          </div>
        </div>
      </div>

      {pendingArticles.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <FaCheck className="text-6xl text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800">Aucun article en attente</h2>
          <p className="text-gray-600">Vous êtes à jour ! Revenez plus tard pour de nouvelles commandes.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {pendingArticles.map((article) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`border-l-4 rounded-lg p-5 cursor-pointer hover:shadow-lg transition-shadow ${getUrgencyColor(article)}`}
              onClick={() => setSelectedArticle(article)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(article)}
                    {article.requirePhoto && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        <FaImage className="inline mr-1" /> Photo requise
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-800">{article.assignmentTitle}</h3>
                  
                  {article.assignmentDescription && (
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{article.assignmentDescription}</p>
                  )}
                  
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <FaCalendarAlt />
                      Deadline: {new Date(article.deadline).toLocaleDateString('fr-FR')}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaClock />
                      {article.isOverdue ? (
                        <span className="text-red-600 font-bold">En retard !</span>
                      ) : article.daysRemaining === 0 ? (
                        <span className="text-orange-600 font-bold">Aujourd'hui !</span>
                      ) : (
                        `${article.daysRemaining} jour(s) restant(s)`
                      )}
                    </span>
                    <span>
                      {article.wordCountMin}-{article.wordCountMax} mots
                    </span>
                  </div>
                </div>
                
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700">
                  {article.status === 'draft' ? 'Continuer' : 'Rédiger'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal de rédaction */}
      <AnimatePresence>
        {selectedArticle && (
          <ArticleEditorModal 
            article={selectedArticle} 
            onClose={() => setSelectedArticle(null)}
            onSaved={() => {
              loadPendingArticles();
              setSelectedArticle(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Modal d'édition d'article
const ArticleEditorModal = ({ article, onClose, onSaved }) => {
  const [formData, setFormData] = useState({
    headline: article.headline || '',
    subheadline: article.subheadline || '',
    lead: article.lead || '',
    content: article.content || '',
    photoCaption: article.photoCaption || ''
  });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    const words = formData.content.trim().split(/\s+/).filter(w => w.length > 0).length;
    setWordCount(words);
  }, [formData.content]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await api.post('/articles/student/save-draft', {
        assignmentId: article.assignmentId,
        ...formData
      });
      alert('Brouillon sauvegardé !');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.headline.trim()) {
      alert('Le titre est obligatoire');
      return;
    }
    if (!formData.lead.trim()) {
      alert('Le chapô est obligatoire');
      return;
    }
    if (!formData.content.trim()) {
      alert('Le contenu est obligatoire');
      return;
    }
    if (wordCount < article.wordCountMin) {
      alert(`L'article doit contenir au moins ${article.wordCountMin} mots (actuellement ${wordCount})`);
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir soumettre cet article ? Vous ne pourrez plus le modifier après soumission.')) {
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/articles/student/submit', {
        assignmentId: article.assignmentId,
        ...formData
      });
      alert('Article soumis avec succès !');
      onSaved();
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getWordCountColor = () => {
    if (wordCount < article.wordCountMin) return 'text-red-600';
    if (wordCount > article.wordCountMax) return 'text-orange-600';
    return 'text-green-600';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{article.assignmentTitle}</h2>
            <p className="text-blue-200 text-sm">
              Deadline: {new Date(article.deadline).toLocaleDateString('fr-FR')} • 
              {article.wordCountMin}-{article.wordCountMax} mots
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Info panel */}
          {(article.suggestedSources?.length > 0 || article.suggestedAngles?.length > 0 || article.assignmentDescription) && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <button 
                onClick={() => setShowSources(!showSources)}
                className="flex items-center gap-2 text-blue-800 font-bold w-full"
              >
                <FaInfoCircle />
                Consignes du professeur
                <span className="ml-auto">{showSources ? '▲' : '▼'}</span>
              </button>
              
              {showSources && (
                <div className="mt-3 space-y-3">
                  {article.assignmentDescription && (
                    <p className="text-gray-700">{article.assignmentDescription}</p>
                  )}
                  
                  {article.suggestedSources?.length > 0 && (
                    <div>
                      <h4 className="font-bold text-blue-800 flex items-center gap-1">
                        <FaLink /> Sources suggérées
                      </h4>
                      <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                        {article.suggestedSources.map((source, i) => (
                          <li key={i}>{source}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {article.suggestedAngles?.length > 0 && (
                    <div>
                      <h4 className="font-bold text-blue-800 flex items-center gap-1">
                        <FaLightbulb /> Angles suggérés
                      </h4>
                      <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                        {article.suggestedAngles.map((angle, i) => (
                          <li key={i}>{angle}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Revision comments if any */}
          {article.status === 'revision_needed' && article.globalComment && (
            <div className="mb-6 bg-orange-50 border border-orange-300 rounded-lg p-4">
              <h4 className="font-bold text-orange-800 flex items-center gap-2">
                <FaExclamationTriangle /> Corrections demandées
              </h4>
              <p className="text-orange-700 mt-2">{article.globalComment}</p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {/* Titre */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                TITRE *
              </label>
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange('headline', e.target.value)}
                placeholder="Un titre accrocheur et informatif"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg font-bold"
              />
            </div>

            {/* Sous-titre */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                SOUS-TITRE
              </label>
              <input
                type="text"
                value={formData.subheadline}
                onChange={(e) => handleChange('subheadline', e.target.value)}
                placeholder="Un complément d'information au titre"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Chapô */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                CHAPÔ * <span className="font-normal text-gray-500">(résumé de l'article en 2-3 phrases)</span>
              </label>
              <textarea
                value={formData.lead}
                onChange={(e) => handleChange('lead', e.target.value)}
                placeholder="Le chapô résume l'essentiel de l'article et donne envie de lire la suite..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Contenu */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                ARTICLE *
                <span className={`float-right font-mono ${getWordCountColor()}`}>
                  {wordCount} / {article.wordCountMin}-{article.wordCountMax} mots
                </span>
              </label>
              <textarea
                ref={contentRef}
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="Rédigez votre article ici. N'oubliez pas de structurer votre texte avec une introduction, un développement et une conclusion..."
                rows={15}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-serif text-lg leading-relaxed"
              />
            </div>

            {/* Photo */}
            {article.requirePhoto && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <label className="block text-sm font-bold text-purple-800 mb-2">
                  <FaImage className="inline mr-2" />
                  PHOTO (obligatoire)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full"
                />
                <input
                  type="text"
                  value={formData.photoCaption}
                  onChange={(e) => handleChange('photoCaption', e.target.value)}
                  placeholder="Légende de la photo (obligatoire)"
                  className="w-full mt-2 px-4 py-2 border border-purple-300 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <span className={getWordCountColor()}>
              {wordCount} mots
            </span>
            {wordCount < article.wordCountMin && (
              <span className="text-red-600 ml-2">
                (minimum {article.wordCountMin})
              </span>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              className="px-6 py-2 border-2 border-gray-300 rounded-lg font-bold hover:bg-gray-100 flex items-center gap-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <FaSave />
              )}
              Sauvegarder brouillon
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={submitting || wordCount < article.wordCountMin}
              className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <FaPaperPlane />
              )}
              Soumettre l'article
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StudentArticlesPage;
